import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Stack,
} from "@mui/material";
import {
  FieldPath,
  FieldValues,
  get,
  useController,
  UseControllerProps,
} from "react-hook-form";

type FormRadioGroupProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RadioGroupProps &
  UseControllerProps<TFieldValues, TName> & {
    options: Readonly<
      Array<{
        name: string;
        value?: string | number | boolean;
      }>
    >;
    placeholder?: string;
  };

export const FormRadioGroup = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  defaultValue,
  shouldUnregister,
  options,
  ...rest
}: FormRadioGroupProps<TFieldValues, TName>) => {
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const errorMsg = get(formState.errors, name)?.message;

  return (
    <FormControl fullWidth={true} error={!!errorMsg}>
      <RadioGroup {...rest} {...field} value={field.value ?? ""}>
        <Stack direction="column" spacing={2}>
          {options.map((option, i) => (
            <FormControlLabel
              key={i}
              value={option.value}
              control={<Radio />}
              label={option.name}
            />
          ))}

          {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
        </Stack>
      </RadioGroup>
    </FormControl>
  );
};
